import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getSources } from "src/apis/ClientAPI";
import { GetTypes } from "src/apis/TypeApis";
import { GetLocations } from "src/apis/locationApis";

const CACHE_EXPIRY = 30 * 60;

interface ReservationFilterData {
  sources: any[];
}

export interface ReservationFiltersState {
  data: ReservationFilterData | null;
  lastFetched: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReservationFiltersState = {
  data: null,
  lastFetched: null,
  loading: false,
  error: null,
};

export const fetchReservationFilters = createAsyncThunk<
  ReservationFilterData,
  { clientId: string | null },
  { rejectValue: string }
>(
  "reservationFilters/fetchReservationFilters",
  async ({ clientId }, { getState, rejectWithValue }) => {
    const state = (getState() as any).reservationFilters;
    const now = Math.floor(Date.now() / 1000);

    if (
      state.data &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_EXPIRY
    ) {
      return state.data;
    }

    if (!clientId) {
      return rejectWithValue("Client ID is required");
    }

    try {
      const sourcesResp = await getSources(clientId, {
        isActive: true,
        module: null,
      });

      const filterData: ReservationFilterData = {
        sources: sourcesResp.data || [],
      };

      return filterData;
    } catch (error) {
      console.error("Error fetching reservation filters:", error);
      return rejectWithValue("Failed to fetch filter data");
    }
  }
);

const reservationFiltersSlice = createSlice({
  name: "reservationFilters",
  initialState,
  reducers: {
    clearReservationFilters: (state) => {
      state.data = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReservationFilters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReservationFilters.fulfilled,
        (state, action: PayloadAction<ReservationFilterData>) => {
          state.loading = false;
          state.data = action.payload;
          state.lastFetched = Math.floor(Date.now() / 1000);
          state.error = null;
        }
      )
      .addCase(fetchReservationFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error";
      });
  },
});

export const { clearReservationFilters } = reservationFiltersSlice.actions;
export default reservationFiltersSlice.reducer;

export const selectReservationFilterData = (state: any) =>
  state.reservationFilters.data;
export const selectReservationFilterLoading = (state: any) =>
  state.reservationFilters.loading;
export const selectReservationFilterLastFetched = (state: any) =>
  state.reservationFilters.lastFetched;
